// src/hooks/useInventory.js
import { useState, useEffect, useCallback } from "react";
import { inventoryService } from "../services/inventoryService";

// Helper: normalizar un producto a { stock, minStock, maxStock }
const normalizeProduct = (p) => {
  if (!p) return p;

  return {
    ...p,
    stock:
      typeof p.stock === "number"
        ? p.stock
        : typeof p.currentStock === "number"
          ? p.currentStock
          : 0,
    minStock:
      typeof p.minStock === "number"
        ? p.minStock
        : typeof p.minimumStock === "number"
          ? p.minimumStock
          : 0,
    maxStock:
      typeof p.maxStock === "number"
        ? p.maxStock
        : typeof p.maximumStock === "number"
          ? p.maximumStock
          : 0,
  };
};

// Helper: extraer array de productos de distintas formas de respuesta
const extractProductsArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.products)) return raw.products;
  if (Array.isArray(raw.items)) return raw.items;
  return [];
};

export const useInventory = (showToast) => {
  const [inventoryData, setInventoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // ✅ Cargar inventario desde backend
  const loadInventory = useCallback(async () => {
    console.log("🔄 useInventory: Cargando productos del backend...");
    setIsLoading(true);
    setError(null);

    try {
      const raw = await inventoryService.getProducts();
      console.log("✅ useInventory: Productos cargados del backend:", raw);

      const products = extractProductsArray(raw);
      const normalizedProducts = products.map(normalizeProduct);

      console.log(
        "📦 useInventory: Productos normalizados:",
        normalizedProducts.length
      );

      setInventoryData(normalizedProducts);
      return normalizedProducts;
    } catch (err) {
      console.error("❌ useInventory: Error cargando productos:", err);
      const message =
        err.message || "Error al conectar con el servidor de inventario";
      setError(message);
      if (showToast) showToast(`❌ ${message}`, "error");
      // ❗ NO volvemos a lanzar el error para que no rompa el Promise.all
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  // ✅ Cargar analytics desde backend (no rompe si falla)
  const loadAnalytics = useCallback(async () => {
    console.log("🔄 useInventory: Cargando analytics...");
    try {
      const raw = await inventoryService.getDashboardAnalytics();
      console.log("✅ useInventory: Analytics cargados:", raw);
      setAnalytics(raw || {});
      return raw;
    } catch (err) {
      console.error("❌ useInventory: Error cargando analytics:", err);
      // No seteamos error global por analytics para no ensuciar la UI
      return null;
    }
  }, []);

  // ✅ Crear producto
  const addProduct = useCallback(
    async (productData) => {
      console.log("🔄 useInventory: Creando producto...", productData);
      setIsLoading(true);
      setError(null);

      try {
        // Mapear datos al formato esperado por el backend
        const backendProduct = {
          code: productData.code || productData.sku || `PROD-${Date.now()}`,
          name: productData.name,
          description: productData.description || "",
          category: productData.category || "general",
          unit: productData.unit || "piece",
          cost: Number(productData.cost) || 0,
          price: Number(productData.price) || 0,
          currentStock:
            Number(productData.currentStock) ||
            Number(productData.stock) ||
            0,
          minimumStock:
            Number(productData.minimumStock) ||
            Number(productData.minStock) ||
            0,
          maximumStock: productData.maximumStock
            ? Number(productData.maximumStock)
            : productData.maxStock
              ? Number(productData.maxStock)
              : null,
          location: productData.location || "default",
          supplier: productData.supplier || "Unknown",
          barcode: productData.barcode || "",
          status: "active",
        };

        console.log(
          "🚀 useInventory - Enviando al backend (createProduct):",
          backendProduct
        );

        const raw = await inventoryService.createProduct(backendProduct);
        console.log("✅ useInventory: Respuesta createProduct:", raw);

        // Soportar varios formatos de respuesta
        const success =
          raw?.success === true ||
          raw?.ok === true ||
          raw?.status === "success" ||
          !raw?.success; // si no manda success, asumimos ok

        if (!success && raw?.error) {
          throw new Error(raw.error);
        }

        const created =
          raw?.data || raw?.product || raw; // si el backend devuelve directamente el objeto

        const normalized = normalizeProduct(created);
        setInventoryData((prev) => [...prev, normalized]);

        if (showToast)
          showToast("✅ Producto agregado correctamente", "success");

        return normalized;
      } catch (err) {
        console.error("❌ useInventory: Error creando producto:", err);
        const message = err.message || "Error al crear producto";
        setError(message);
        if (showToast) showToast(`❌ ${message}`, "error");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast]
  );

  // ✅ Actualizar producto
  const updateProduct = useCallback(
    async (productId, updates) => {
      console.log("🔄 useInventory: Actualizando producto...", {
        productId,
        updates,
      });
      setIsLoading(true);
      setError(null);

      try {
        const backendUpdates = {
          ...updates,
        };

        if (updates.stock !== undefined) {
          backendUpdates.currentStock = Number(updates.stock);
        }
        if (updates.minStock !== undefined) {
          backendUpdates.minimumStock = Number(updates.minStock);
        }
        if (updates.maxStock !== undefined) {
          backendUpdates.maximumStock = Number(updates.maxStock);
        }

        Object.keys(backendUpdates).forEach((k) => {
          if (backendUpdates[k] === undefined) delete backendUpdates[k];
        });

        const raw = await inventoryService.updateProduct(
          productId,
          backendUpdates
        );
        console.log("✅ useInventory: Respuesta updateProduct:", raw);

        const success =
          raw?.success === true ||
          raw?.ok === true ||
          raw?.status === "success" ||
          !raw?.success;

        if (!success && raw?.error) {
          throw new Error(raw.error);
        }

        const updatedRaw = raw?.data || raw?.product || raw;
        const updated = normalizeProduct(updatedRaw);

        setInventoryData((prev) =>
          prev.map((item) =>
            item._id === productId || item.id === productId
              ? { ...item, ...updated }
              : item
          )
        );

        if (showToast) showToast("✅ Producto actualizado", "success");
        return updated;
      } catch (err) {
        console.error("❌ useInventory: Error actualizando producto:", err);
        const message = err.message || "Error al actualizar producto";
        setError(message);
        if (showToast) showToast(`❌ ${message}`, "error");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast]
  );

  // ✅ Eliminar producto
  const deleteProduct = useCallback(
    async (productId) => {
      console.log("🗑️ useInventory: Eliminando producto...", productId);
      const prevData = [...inventoryData];
      setError(null);

      // Optimistic update
      setInventoryData((prev) =>
        prev.filter(
          (i) => i._id !== productId && i.id !== productId
        )
      );
      setIsLoading(true);

      try {
        const raw = await inventoryService.deleteProduct(productId);
        console.log("✅ useInventory: Respuesta deleteProduct:", raw);

        const success =
          raw?.success === true ||
          raw?.ok === true ||
          raw?.status === "success" ||
          !raw?.success;

        if (!success && raw?.error) {
          throw new Error(raw.error);
        }

        if (showToast) showToast("✅ Producto eliminado", "success");
      } catch (err) {
        console.error("❌ useInventory: Error al eliminar:", err);
        const message = err.message || "Error al eliminar producto";
        setError(message);
        setInventoryData(prevData); // rollback
        if (showToast) showToast(`❌ ${message}`, "error");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [inventoryData, showToast]
  );

  // ✅ Obtener producto por ID
  const getProductById = useCallback(async (productId) => {
    try {
      const raw = await inventoryService.getProductById(productId);
      const p = raw?.data || raw?.product || raw;
      return normalizeProduct(p);
    } catch (err) {
      console.error(
        "❌ useInventory: Error al obtener producto por ID:",
        err
      );
      throw err;
    }
  }, []);

  // 🔍 Buscar localmente
  const searchProducts = useCallback(
    (term) => {
      if (!term) return inventoryData;
      const t = term.toLowerCase();
      return inventoryData.filter(
        (i) =>
          i.name?.toLowerCase().includes(t) ||
          i.code?.toLowerCase().includes(t) ||
          i.category?.toLowerCase().includes(t) ||
          i.supplier?.toLowerCase().includes(t)
      );
    },
    [inventoryData]
  );

  // 🔄 Refrescar datos
  const refreshData = useCallback(async () => {
    await Promise.all([loadInventory(), loadAnalytics()]);
  }, [loadInventory, loadAnalytics]);

  // 📥 Cargar al montar
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // 🧹 Limpiar error
  const clearError = useCallback(() => setError(null), []);

  // 📊 Métricas
  const computeMetrics = useCallback(() => {
    const products = inventoryData;
    return {
      totalProducts: products.length,
      lowStockProducts: products.filter(
        (p) => (p.stock || 0) <= (p.minStock || 0)
      ).length,
      outOfStockProducts: products.filter(
        (p) => (p.stock || 0) === 0
      ).length,
      totalInventoryValue: products.reduce(
        (sum, p) => sum + (p.cost || 0) * (p.stock || 0),
        0
      ),
      categories: [...new Set(products.map((p) => p.category))],
      suppliers: [...new Set(products.map((p) => p.supplier))],
      locations: [...new Set(products.map((p) => p.location))],
    };
  }, [inventoryData]);

  const metrics = computeMetrics();

  return {
    inventoryData,
    analytics,
    isLoading,
    error,
    loadInventory,
    loadAnalytics,
    refreshData,
    addProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    searchProducts,
    clearError,
    metrics,
    ...metrics,
  };
};
